import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Cell, HtmlTable } from '../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Checkbox } from '../../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { IconButton } from '../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { useCreateOrUpdateEstatePlanningDocumentMutation } from '../../../../api/useCreateOrUpdateEstatePlanningDocumentMutation';
import { useIsPdf } from '../../../../../../../hooks/useIsPdf';
import { EstatePlanningDocumentDTO } from '../../../../../../../types/goalsDTO';
import { useClientId } from '../../../../../../../../_hooks/useClientId';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Loading } from '../../../../../../../../CommonComponents/OPL/Loading/Loading';
import { formatDate } from '../../../../../../../../CommonUtils/formatDate';

interface Props {
  name: string;
  documents: EstatePlanningDocumentDTO[];
  onSelectedDocument: (document: EstatePlanningDocumentDTO) => void;
  onSetDocumentToDelete: (document: EstatePlanningDocumentDTO) => void;
}

export const ChecklistTable = ({
  name,
  documents,
  onSelectedDocument,
  onSetDocumentToDelete,
}: Props) => {
  const {
    mutateAsync: updateEstateDocument,
    isLoading: isSaving,
  } = useCreateOrUpdateEstatePlanningDocumentMutation();

  const isPrint = useIsPdf();

  const clientId = useClientId();

  return (
    <HtmlTable mt="24">
      <table>
        <colgroup>
          <col width="1px" />
          <col width="50%" />
          <col width="50%" />
          <col width="1px" />
        </colgroup>

        <caption>
          <Box display="flex" alignItems="center" css="grid-gap: 8px">
            <Box>{name}</Box>

            <AnimatePresence>
              {isSaving && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Loading size="small" />
                </motion.span>
              )}
            </AnimatePresence>
          </Box>
        </caption>

        <thead>
          <tr>
            <th aria-label="Select Checkbox" />
            <th>
              <Text variant="smallCaps" textAlign="left">
                Document Name
              </Text>
            </th>
            <th>
              <Text variant="smallCaps" textAlign="left">
                Last Updated
              </Text>
            </th>
            <th aria-label="Action Items" />
          </tr>
        </thead>
        <tbody>
          {documents.map(document => (
            <tr key={document.Id}>
              <Cell css="white-space: nowrap;">
                <Checkbox
                  checked={document.Checked}
                  onChange={ev =>
                    updateEstateDocument({
                      clientId: clientId.toString(),
                      ...document,
                      Checked: ev.target.checked,
                    })
                  }
                  aria-label="Select Document"
                />
              </Cell>
              <Cell css="white-space: nowrap;">{document.Description ?? name}</Cell>
              <Cell css="white-space: nowrap;">
                {document.LastDocumentUpdate ? formatDate(document.LastDocumentUpdate) : '—'}
              </Cell>
              <Cell css="white-space: nowrap;">
                {!isPrint && (
                  <>
                    <IconButton
                      variant="subtleInactive"
                      icon="far fa-pencil"
                      onClick={() => onSelectedDocument(document)}
                      aria-label="Edit Document"
                    />

                    {document.Id !== null && (
                      <IconButton
                        variant="subtleInactive"
                        icon="far fa-minus-circle"
                        onClick={async () => onSetDocumentToDelete(document)}
                        ml="8"
                        aria-label="Delete Document"
                      />
                    )}
                  </>
                )}
              </Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </HtmlTable>
  );
};
